import { observer } from "mobx-react";
import { Document } from "~/document";
import docStore from "~/stores/document";
import { ReqStatus } from "~/api";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Spinner } from "~/components/ui/spinner";
import { Check } from "lucide-react";
import { useNavigate } from "react-router";
import paths from "~/paths";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

const DocumentArchiveModal = observer(
  (props: {
    document: Document;
    open: boolean;
    close: () => void;
    unArchive?: boolean;
    onSuccess: () => Promise<unknown>;
  }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const onDelete = async () => {
      if (props.unArchive) navigate(paths.construct.editDocument(props.document.protocol));
      else await docStore.archiveDocument(props.document.protocol);
    };
    const status = props.unArchive ? docStore.documentRepublishingStatus : docStore.documentArchiveStatus;

    const onClose = () => {
      if (status === ReqStatus.Success) {
        props.onSuccess();
      }
      props.close();
    };

    useEffect(() => {
      docStore.documentArchiveStatus = ReqStatus.Initial;
      docStore.documentRepublishingStatus = ReqStatus.Initial;
    }, [id]);

    const content = (() => {
      switch (status) {
        case ReqStatus.Initial: {
          return (
            <div className="space-y-2 text-base">
              {props.unArchive ? (
                <>
                  <p className="font-semibold">Are you sure you want to re-publish this document?</p>
                  <p className="font-semibold">
                    Re-publishing this document will render it searchable and accessible to readers
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold">Are you sure you want to archive this document?</p>
                  <p className="font-semibold">Archiving the document will remove it from the search.</p>
                </>
              )}
            </div>
          );
        }
        case ReqStatus.InProcess: {
          return (
            <div className="flex items-center gap-2">
              <Spinner className="size-5" />
              <p className="text-base">{props.unArchive ? "Re-publishing" : "Archiving"} document...</p>
            </div>
          );
        }
        case ReqStatus.Success: {
          return (
            <div className="flex items-center gap-2">
              <Check className="size-6 text-success-600" />
              <p className="text-base">Successfully {props.unArchive ? "Re-published" : "Archived"} the document</p>
            </div>
          );
        }
        case ReqStatus.Failed: {
          return (
            <div className="flex items-center gap-2">
              <p className="text-base">Failed to {props.unArchive ? "Re-publish" : "Archive"} the document</p>
            </div>
          );
        }
        default: {
          return null;
        }
      }
    })();

    const deletingDocument = docStore.documentDeletionStatus === ReqStatus.InProcess;

    const footer = () => {
      switch (status) {
        case ReqStatus.Initial:
        case ReqStatus.InProcess: {
          return (
            <>
              <Button variant="secondary" onClick={onClose} disabled={deletingDocument}>
                Go Back
              </Button>
              <Button variant="default" onClick={onDelete} disabled={deletingDocument}>
                Continue
              </Button>
            </>
          );
        }
        case ReqStatus.Success: {
          return (
            <Button variant="default" onClick={onClose}>
              Done
            </Button>
          );
        }
        default: {
          return null;
        }
      }
    };

    return (
      <Dialog
        open={props.open}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent className="sm:max-w-[480px]" aria-describedby={undefined}>
          <DialogHeader>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Document management</p>
            <DialogTitle>{props.unArchive ? "Re-publish document" : "Archive document"}</DialogTitle>
          </DialogHeader>
          <div className="min-h-[56px]">{content}</div>
          <DialogFooter>{footer()}</DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);

export default DocumentArchiveModal;
